import React, { useRef, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { StatNavItem, SeasonNavItem } from './components/nav-items';
import { Heading, DataTypeToggle, dataTypeOptions } from './components/header';
import { PerformanceChartContainer } from './components/chart';

import { usePerformanceChartNavSync } from './hooks/use-performance-chart-nav-sync';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';

import {
    performanceChartNavSelector,
    performanceChartDataTypeSelector
} from 'store/modules/statspro/performance-chart/selectors';
import { playersSelector } from 'store/modules/statspro/player-comparison/selectors';
import { useGetCompSeasonQuery } from 'common/store/modules/api/afl-api';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { UPDATE_NAV_ITEM } from 'store/modules/statspro/performance-chart/actions';
import { getTranslation } from 'common/react/utils/translations';
import { STATS_CATEGORY } from '../common/config/performance-stats';

const NavWrapper = ({ children }) => (
    <div className="competition-nav competition-nav--no-margin-top filter-dropdown">
        {children}
    </div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const PerformanceChart = ({
    title,
    type,
    seasonId,
    playerProviderId
}) => {
    const isAFLW = PULSE.app.common.url.checkBaseRoute('aflw');

    const { data: compData } = useGetCompSeasonQuery(
        { id: seasonId },
        {
            skip: !seasonId
        }
    );
    const dispatch = useDispatch();
    const nav = useSelector(performanceChartNavSelector);
    const dataType = useSelector(performanceChartDataTypeSelector);
    const options = useCompetitionNavigationOptions(nav);
    const windowSize = useWindowSize();
    const isResponsive = () =>
        windowSize.width < PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    usePerformanceChartNavSync(options, compData?.competition.id);

    const playerId = playerProviderId;
    const playerIdsFromStore = useSelector(playersSelector);
    const ref = useRef();

    const playerIds = useMemo(
        () => (playerId ? { 1: playerId, 2: null } : playerIdsFromStore),
        [playerId, playerIdsFromStore]
    );

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            category: nav.category,
            season: nav.season,
            dataType
        },
        options: {
            ...options,
            dataTypes: dataTypeOptions
        },
        filtersFields: ['performancechart-stat', 'career-season', 'dataType'],
        filtersRef: ref
    });

    const getInitialCategoryId = () => {
        return STATS_CATEGORY.AFL_FANTASY_POINTS; // Default category from initial state
    };

    const getInitialSeasonId = () => {
        // If compseasonId is provided, that's the intended initial season
        // Otherwise, default to -1 (career view) which is the reducer's initial state
        return -1;
    };

    const hasChangedFromDefaults = () => {
        if (!nav?.category || !nav?.season) {
            return false;
        }

        const initialCategoryId = getInitialCategoryId();
        const initialSeasonId = getInitialSeasonId();

        // Only check for changes if we have valid initial values
        if (
            !initialCategoryId ||
            (initialSeasonId !== -1 && !initialSeasonId)
        ) {
            return false;
        }
        const isCategoryChanged = nav?.category !== initialCategoryId;
        const isSeasonChanged = nav?.season?.id !== initialSeasonId;
        return isCategoryChanged || isSeasonChanged;
    };

    const resetToDefaults = () => {
        const initialCategoryId = getInitialCategoryId();
        const initialSeasonId = getInitialSeasonId();

        if (nav?.category !== initialCategoryId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'category',
                    value: initialCategoryId
                })
            );
        }
        if (nav?.season?.id !== initialSeasonId) {
            // Only reset season if we have a valid initial season ID
            if (
                initialSeasonId !== null &&
                typeof initialSeasonId !== 'undefined'
            ) {
                dispatch(
                    UPDATE_NAV_ITEM.request({
                        navItem: 'season',
                        value: { id: initialSeasonId }
                    })
                );
            }
        }
    };

    return (
        <div
            className={classNames('fade-in-on-load', {
                'fade-in-on-load fade-in-on-load--stats comparison-chart__wrapper':
                    !playerId,
                'is-loaded': playerIds[1] || playerIds[2]
            })}
            ref={ref}
        >
            {playerIds[1] || playerIds[2] ? (
                <StatsSection.Main NavWrapper={NavWrapper}>
                    <StatsSection.Heading title={title} />
                    <StatsSection.Nav
                        showResponsiveItems={showResponsiveItems}
                        withScrollableFilter={!showResponsiveItems}
                    >
                        <StatsSection.NavItems>
                            <StatNavItem />
                            <SeasonNavItem />
                            {hasChangedFromDefaults() &&
                            !showResponsiveItems ? (
                                <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                    <button
                                        className="stats-leaders-filter-reset-button"
                                        onClick={resetToDefaults}
                                    >
                                        {getTranslation(
                                            'label.statsLeaders.filters.resetButtonText'
                                        )}
                                    </button>
                                </div>
                            ) : null}
                        </StatsSection.NavItems>
                        {hasChangedFromDefaults() && showResponsiveItems ? (
                            <StatsSection.NavFilterItems>
                                <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                    <button
                                        className="stats-leaders-filter-reset-button"
                                        onClick={resetToDefaults}
                                    >
                                        {getTranslation(
                                            'label.statsLeaders.filters.resetButtonText'
                                        )}
                                    </button>
                                </div>
                            </StatsSection.NavFilterItems>
                        ) : null}
                    </StatsSection.Nav>
                    <StatsSection.Content>
                        <StatsSection.ContentHeader>
                            <StatsSection.ContentHeaderHeading>
                                <Heading />
                            </StatsSection.ContentHeaderHeading>
                            <StatsSection.ContentHeaderOptions>
                                <DataTypeToggle />
                            </StatsSection.ContentHeaderOptions>
                        </StatsSection.ContentHeader>
                        <StatsSection.ContentBody>
                            <PerformanceChartContainer
                                playerIds={playerIds}
                                type={type}
                                containerRef={ref}
                                isAFLW={isAFLW}
                                compseasonPid={compData?.providerId}
                            />
                        </StatsSection.ContentBody>
                    </StatsSection.Content>
                </StatsSection.Main>
            ) : null}
        </div>
    );
};

PerformanceChart.propTypes = {
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
    seasonId: PropTypes.string,
    playerProviderId: PropTypes.string
};
